'use client';

import { useEffect, useState } from 'react';

const CHECKSHOP_SHOP_ID = 'cmpk1hwes000z7cmr241ig998';
const CHECKSHOP_DOMAIN = 'printen.eu';

type CheckShopBadgeData = {
  score?: number;
  avgRating?: number;
  reviewCount?: number;
  domain?: string;
};

function displayScore(data: CheckShopBadgeData | null) {
  if (typeof data?.avgRating === 'number' && data.avgRating > 0) {
    return (data.avgRating * 2).toFixed(1);
  }
  if (typeof data?.score === 'number') {
    return String(data.score);
  }
  return '-';
}

function scoreSuffix(data: CheckShopBadgeData | null) {
  return typeof data?.avgRating === 'number' && data.avgRating > 0 ? '/10' : '/100';
}

function CheckshopShield() {
  const gradientId = 'checkshop-floating-gradient';
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="32" y1="3" x2="32" y2="61" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="55%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
      </defs>
      <path d="M32 3 L57 11 Q57 16 57 30 Q57 49 32 61 Q7 49 7 30 Q7 16 7 11 Z" fill={`url(#${gradientId})`} />
      <path d="M20 32.5 L29 41.5 L45.5 23" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function tabCopy(locale: string) {
  const lang = locale.slice(0, 2).toLowerCase();
  if (lang === 'de') return { reviews: 'Bewertungen', view: 'Bewertungen ansehen', close: 'Schließen' };
  if (lang === 'fr') return { reviews: 'avis', view: 'Voir les avis', close: 'Fermer' };
  if (lang === 'en') return { reviews: 'reviews', view: 'View reviews', close: 'Close' };
  return { reviews: 'reviews', view: 'Bekijk reviews', close: 'Sluiten' };
}

export function CheckshopFloatingTab({ side = 'right', locale = 'nl' }: { side?: 'left' | 'right'; locale?: string }) {
  const [data, setData] = useState<CheckShopBadgeData | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(`https://checkshop.eu/api/v1/badge/${CHECKSHOP_SHOP_ID}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((badgeData) => {
        if (!cancelled && badgeData && !badgeData.error) setData(badgeData);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const rightSide = side !== 'left';
  const score = displayScore(data);
  const domain = data?.domain || CHECKSHOP_DOMAIN;
  const reviewCount = data?.reviewCount || 0;
  const copy = tabCopy(locale);

  return (
    <div
      id="checkshop-tab"
      className="fixed top-1/2 z-[99999] -translate-y-1/2 font-sans"
      style={rightSide ? { right: 0 } : { left: 0 }}
    >
      <button
        type="button"
        className={`cs-tab cs-collapsed flex h-9 w-9 flex-col items-center justify-center overflow-hidden border-0 bg-white/95 px-1 py-[3px] text-slate-900 shadow-lg ${rightSide ? 'rounded-l-lg' : 'rounded-r-lg'}`}
        style={{ ['--cs-hover-x' as string]: rightSide ? '-3px' : '3px' }}
        aria-label={`CheckShop ${score}${scoreSuffix(data)}`}
        onClick={() => setOpen((value) => !value)}
      >
        <CheckshopShield />
        <span className="text-xs font-black leading-none tracking-normal">{score}</span>
      </button>

      {open && (
        <div
          className={`absolute top-1/2 w-[280px] -translate-y-1/2 rounded-2xl bg-white p-5 text-center shadow-2xl ${rightSide ? 'right-10' : 'left-10'}`}
        >
          <button
            type="button"
            aria-label={copy.close}
            className="absolute right-3 top-2 text-xl leading-none text-slate-400 hover:text-slate-700"
            onClick={() => setOpen(false)}
          >
            &times;
          </button>
          <div className="mb-3 flex items-center justify-center gap-2 text-sm font-black text-slate-900">
            <CheckshopShield />
            CheckShop
          </div>
          <div className="text-5xl font-black leading-none tracking-normal text-slate-950">{score}</div>
          <div className="mt-1 text-sm font-semibold text-slate-400">{scoreSuffix(data)}</div>
          <div className="mt-3 text-sm font-semibold text-emerald-700">
            {reviewCount > 0 ? `${reviewCount} ${copy.reviews}` : copy.reviews}
          </div>
          <a
            href={`https://checkshop.eu/shop/${domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 block rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white no-underline"
          >
            {copy.view}
          </a>
        </div>
      )}
    </div>
  );
}
